import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { translations, languages, getCurrentLanguage, setCurrentLanguage, t } from './translations.js';
import { initAssetViewer, openAssetViewer, closeAssetViewer, getCurrentAsset } from './assetViewer.js';
import { getBuilding, hasAssets } from './buildingsLibrary.js';

// VERSION TRACKING
const VERSION = '5.0 - Production MVP';

// Global variables
let scene, camera, renderer, controls;
let model, mixer, clock;
let hitTestSource = null;
let hitTestSourceRequested = false;
let reticle;
let modelPlaced = false;
let bgMesh = null; // gradient background sphere
let viewerScale = 1; // normalized scale for the 3D viewer
let originalCenter = new THREE.Vector3(); // original model center for viewer repositioning

// AR touch gesture state
let arTouches = new Map(); // tracks active XR input sources for gestures
let arModelScale = 1; // current AR pinch-to-scale multiplier
let arPrevTouchData = null; // previous frame touch data for delta calculations

// Turntable animation settings
let turntableEnabled = true;
const turntableSpeed = 0.05;

// Brightness fade-in settings
const BRIGHTNESS_FADE_DURATION = 10000; // ms
let brightnessFadeStart = 0;
let brightnessFading = false;

// UI Elements
const languageSwitcher = document.getElementById('language-switcher');
const languageCurrent = document.getElementById('language-current');
const languageDropdown = document.getElementById('language-dropdown');
const languageOptions = document.querySelectorAll('.language-option');
const currentLangFlag = document.getElementById('current-lang-flag');

// Detect iOS
// Detect iOS + iPadOS (iPadOS 13+ reports as "Macintosh" in user agent)
const isIOS = (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// ============================================
// LANGUAGE SYSTEM
// ============================================

function initLanguageDropdown() {
  // Language auto-detected from navigator.language if not set (see translations.js)
  updateLanguageUI();
  updateActiveLanguageOption();

  if (languageCurrent) {
    languageCurrent.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleDropdown();
    });
  }

  languageOptions.forEach(option => {
    option.addEventListener('click', (e) => {
      e.stopPropagation();
      const lang = option.dataset.lang;
      selectLanguage(lang);
    });
  });

  document.addEventListener('click', (e) => {
    if (!languageSwitcher.contains(e.target)) {
      closeDropdown();
    }
  });
}

function toggleDropdown() {
  const isOpen = languageSwitcher.classList.contains('open');
  if (isOpen) {
    closeDropdown();
  } else {
    openDropdown();
  }
}

function openDropdown() {
  languageSwitcher.classList.add('open');
  languageDropdown.classList.remove('hidden');
}

function closeDropdown() {
  languageSwitcher.classList.remove('open');
  languageDropdown.classList.add('hidden');
}

function selectLanguage(langCode) {
  setCurrentLanguage(langCode);
  updateLanguageUI();
  updateActiveLanguageOption();
  closeDropdown();
  if (window.stopSecessionAudio) window.stopSecessionAudio();
  console.log('Language selected:', langCode);
}

function updateActiveLanguageOption() {
  const currentLang = getCurrentLanguage();
  languageOptions.forEach(option => {
    if (option.dataset.lang === currentLang) {
      option.classList.add('active');
    } else {
      option.classList.remove('active');
    }
  });
}

function updateLanguageUI() {
  const lang = getCurrentLanguage();
  const langData = languages.find(l => l.code === lang);

  // Update language switcher flag
  if (currentLangFlag && langData) {
    currentLangFlag.src = langData.flag;
    currentLangFlag.alt = langData.name;
  }

  // Update all data-i18n elements
  document.querySelectorAll('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n');
    const translated = t(key);
    if (translated) {
      if (el.hasAttribute('data-i18n-html')) {
        el.innerHTML = translated;
      } else {
        el.textContent = translated;
      }
    }
  });

  // Update subtitle (has HTML content)
  const subtitleEl = document.getElementById('buildings-subtitle');
  if (subtitleEl) subtitleEl.innerHTML = t('subtitle');

  console.log('UI updated for language:', lang);
}

// ============================================
// GRADIENT BACKGROUND
// ============================================

function createGradientBackground() {
  const bgGeometry = new THREE.SphereGeometry(50, 32, 32);
  const bgMaterial = new THREE.ShaderMaterial({
    uniforms: {
      topColor: { value: new THREE.Color(0x324B3E) },
      middleColor: { value: new THREE.Color(0x3d5a4a) },
      bottomColor: { value: new THREE.Color(0x324B3E) },
    },
    vertexShader: `
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 topColor;
      uniform vec3 middleColor;
      uniform vec3 bottomColor;
      varying vec3 vWorldPosition;
      void main() {
        float h = normalize(vWorldPosition).y;
        vec3 color;
        if (h > 0.0) {
          color = mix(middleColor, topColor, h);
        } else {
          color = mix(middleColor, bottomColor, -h);
        }
        gl_FragColor = vec4(color, 1.0);
      }
    `,
    side: THREE.BackSide,
    depthWrite: false
  });

  bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
  scene.add(bgMesh);
}

// ============================================
// INITIALIZATION
// ============================================

// Expose for inline scripts (Secession audio)
window.__getCurrentLanguage = getCurrentLanguage;

initLanguageDropdown();
init();
initAssetViewer();

// ============================================
// ASSET VIEWER EVENT LISTENERS
// ============================================

window.addEventListener('openAssetViewer', (e) => {
  const { buildingId } = e.detail;
  console.log('[Main] Opening asset viewer for building:', buildingId);

  if (!hasAssets(buildingId)) {
    console.warn('[Main] Building has no assets yet:', buildingId);
    alert('No assets available for this building yet. Coming soon!');
    window.showBuildingsScreen();
    return;
  }

  openAssetViewer(buildingId);
});

window.addEventListener('loadAssetModel', (e) => {
  const { modelPath, modelUsdz, hasAnimation, turntable } = e.detail;
  console.log('[Main] Loading asset model:', modelPath);
  unloadCurrentModel();
  loadAssetModel(modelPath, modelUsdz, hasAnimation, turntable);
});

window.addEventListener('unloadCurrentModel', () => {
  console.log('[Main] Unloading current model');
  unloadCurrentModel();
});

window.addEventListener('assetViewerClosed', () => {
  console.log('[Main] Asset viewer closed');
  unloadCurrentModel();
});

window.addEventListener('modelReveal', () => {
  console.log('[Main] Starting brightness fade-in');
  if (model) model.visible = true;
  brightnessFadeStart = performance.now();
  brightnessFading = true;
});

// ============================================
// MODEL MANAGEMENT
// ============================================

function unloadCurrentModel() {
  if (model) {
    scene.remove(model);

    model.traverse((child) => {
      if (child.isMesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });

    model = null;
  }

  if (mixer) {
    mixer.stopAllAction();
    mixer = null;
  }
}

function loadAssetModel(modelPath, modelUsdz, hasAnimation = false, useTurntable = true) {
  const loader = new GLTFLoader();

  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
  loader.setDRACOLoader(dracoLoader);

  const loadingEl = document.getElementById('loading');
  if (loadingEl) loadingEl.classList.remove('hidden');

  loader.load(
    modelPath,
    (gltf) => {
      model = gltf.scene;

      // Auto-scale and center the model
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const maxDim = Math.max(size.x, size.y, size.z);
      console.log('[Main] Model dimensions (meters):', {
        x: size.x.toFixed(3), y: size.y.toFixed(3), z: size.z.toFixed(3),
        maxDim: maxDim.toFixed(3)
      });
      const scale = 2 / maxDim;
      viewerScale = scale;
      originalCenter = center.clone();
      model.scale.setScalar(scale);

      model.position.set(0, 0, 0);
      model.position.x = -center.x * scale;
      model.position.y = -center.y * scale;
      model.position.z = -center.z * scale;

      // Set emissive intensity to 0 and hide until reveal
      model.visible = false;
      model.traverse((child) => {
        if (child.isMesh) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((mat) => {
            if ('emissiveIntensity' in mat) mat.emissiveIntensity = 0;
          });
        }
      });

      scene.add(model);

      // Reset camera position
      if (camera) {
        camera.position.set(0, 0, 3.5);
        camera.lookAt(0, 0, 0);
      }

      if (controls) {
        controls.target.set(0, 0, 0);
        controls.update();
      }

      // Setup animation if available, otherwise use turntable
      if (gltf.animations && gltf.animations.length > 0 && hasAnimation) {
        mixer = new THREE.AnimationMixer(model);
        gltf.animations.forEach((clip) => {
          mixer.clipAction(clip).play();
        });
        turntableEnabled = false;
      } else {
        turntableEnabled = useTurntable;
      }

      if (loadingEl) loadingEl.classList.add('hidden');

      // Update iOS AR link if available
      if (modelUsdz) {
        const iosArLink = document.getElementById('asset-ios-ar-link');
        if (iosArLink) {
          iosArLink.href = modelUsdz;
        }
      }

      console.log('[Main] Model loaded successfully');
    },
    (progress) => {
      if (progress.total > 0) {
        const percent = (progress.loaded / progress.total * 100).toFixed(0);
        console.log('[Main] Loading:', percent + '%');
      }
    },
    (error) => {
      console.error('[Main] Error loading model:', error);
      if (loadingEl) loadingEl.classList.add('hidden');
    }
  );
}

// ============================================
// SCENE INITIALIZATION
// ============================================

function init() {
  scene = new THREE.Scene();
  createGradientBackground();

  camera = new THREE.PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    1000
  );
  camera.position.set(0, 0, 3.5);

  renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true
  });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.outputEncoding = THREE.sRGBEncoding;
  // XR stays disabled until user taps AR — avoids "already active session" conflicts
  renderer.xr.enabled = false;

  document.getElementById('canvas-container').appendChild(renderer.domElement);

  // Simple ambient light as fallback (unlit models ignore lighting)
  const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
  scene.add(ambientLight);

  // Add orbit controls
  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.minDistance = 1;
  controls.maxDistance = 10;
  controls.target.set(0, 0, 0);
  controls.update();

  // Create reticle for AR
  reticle = new THREE.Mesh(
    new THREE.RingGeometry(0.15, 0.2, 32).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide })
  );
  reticle.matrixAutoUpdate = false;
  reticle.visible = false;
  scene.add(reticle);

  // Setup AR
  setupAR();

  clock = new THREE.Clock();

  window.addEventListener('resize', onWindowResize, false);

  animate();
}

// ============================================
// AR SETUP
// ============================================

function setupAR() {
  const hasXR = 'xr' in navigator;
  const assetArButton = document.getElementById('asset-ar-button');

  console.log('=== AR DETECTION ===');
  console.log('Is iOS:', isIOS);
  console.log('Has navigator.xr:', hasXR);

  if (isIOS) {
    console.log('Device: iOS - Quick Look available via asset viewer');
    return;
  }

  if (hasXR) {
    navigator.xr.isSessionSupported('immersive-ar').then((supported) => {
      console.log('WebXR immersive-ar supported:', supported);
      if (supported) {
        // Mobile with AR support — start AR session on click
        if (assetArButton) {
          assetArButton.addEventListener('click', startAR);
        }
        console.log('Android with ARCore - AR available');
      } else {
        // Desktop/device without AR — show QR popup
        setupArQrPopup(assetArButton);
        console.log('Desktop/no AR - QR popup will be shown');
      }
    }).catch((err) => {
      console.warn('Error checking AR support:', err);
      // Fallback: show QR popup
      setupArQrPopup(assetArButton);
    });
  } else {
    // No WebXR at all (older desktop browsers) — show QR popup
    setupArQrPopup(assetArButton);
    console.log('No WebXR API - QR popup will be shown');
  }
}

function setupArQrPopup(arButton) {
  const popup = document.getElementById('ar-qr-popup');
  const closeBtn = document.getElementById('ar-qr-popup-close');
  if (!popup) return;

  if (arButton) {
    arButton.addEventListener('click', () => {
      popup.classList.remove('hidden');
      // Trigger reflow then show
      requestAnimationFrame(() => popup.classList.add('visible'));
    });
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      popup.classList.remove('visible');
      setTimeout(() => popup.classList.add('hidden'), 300);
    });
  }

  // Close on backdrop click
  popup.addEventListener('click', (e) => {
    if (e.target === popup) {
      popup.classList.remove('visible');
      setTimeout(() => popup.classList.add('hidden'), 300);
    }
  });
}

async function startAR() {
  try {
    console.log('Requesting AR session...');

    // Hide background sphere BEFORE session starts so first frame is transparent
    if (bgMesh) bgMesh.visible = false;

    // Prepare model for AR: restore original scale (1:1 real-world), reset rotation, and hide until placed
    if (model) {
      model.scale.setScalar(1); // real-world scale from Reality Capture
      model.rotation.set(0, 0, 0); // reset to file's default facing direction
      model.visible = false;
      modelPlaced = false;
    }

    // Enable XR on the renderer
    renderer.xr.enabled = true;
    renderer.xr.setReferenceSpaceType('local');

    const session = await navigator.xr.requestSession('immersive-ar', {
      requiredFeatures: ['local', 'hit-test'],
      optionalFeatures: ['light-estimation']
    });

    await renderer.xr.setSession(session);

    // Hide all DOM UI so it doesn't cover the camera feed
    document.getElementById('asset-viewer-screen').style.background = 'transparent';
    document.getElementById('canvas-container').style.background = 'transparent';
    document.body.style.background = 'transparent';

    // Hide asset viewer UI elements
    const assetUiElements = document.querySelectorAll('.asset-ui-element');
    assetUiElements.forEach(el => el.style.display = 'none');

    session.addEventListener('end', onSessionEnd);

    // Setup touch gesture tracking for rotate/scale
    arTouches.clear();
    arModelScale = 1;
    arPrevTouchData = null;

    session.addEventListener('selectstart', (e) => {
      const inputSource = e.inputSource;
      if (inputSource.targetRayMode === 'screen') {
        arTouches.set(inputSource, { startTime: performance.now() });
      }
    });

    session.addEventListener('selectend', (e) => {
      const inputSource = e.inputSource;
      arTouches.delete(inputSource);
      // Reset previous touch data when finger count changes
      arPrevTouchData = null;
    });

    console.log('AR session started');

  } catch (err) {
    console.error('Failed to start AR:', err.message);
    // Restore state if session failed to start
    renderer.xr.enabled = false;
    if (bgMesh) bgMesh.visible = true;
    if (model) model.visible = true;
  }
}

function onSessionEnd() {
  console.log('AR session ended');
  hitTestSourceRequested = false;
  hitTestSource = null;
  modelPlaced = false;

  // Clear AR gesture state
  arTouches.clear();
  arModelScale = 1;
  arPrevTouchData = null;

  // Disable XR so renderer goes back to normal requestAnimationFrame
  renderer.xr.enabled = false;

  // Restore background sphere
  if (bgMesh) bgMesh.visible = true;

  // Restore DOM backgrounds
  document.body.style.background = '';
  document.getElementById('asset-viewer-screen').style.background = '';
  document.getElementById('canvas-container').style.background = '';

  // Restore asset viewer UI elements
  const assetUiElements = document.querySelectorAll('.asset-ui-element');
  assetUiElements.forEach(el => el.style.display = '');


  if (model) {
    // Restore viewer scale and centering
    model.scale.setScalar(viewerScale);
    model.position.set(
      -originalCenter.x * viewerScale,
      -originalCenter.y * viewerScale,
      -originalCenter.z * viewerScale
    );
    model.rotation.set(0, 0, 0);
    model.visible = true;
  }

  camera.position.set(0, 0, 3.5);

  if (controls) {
    controls.target.set(0, 0, 0);
    controls.update();
  }

  if (reticle) {
    reticle.visible = false;
  }
}

// ============================================
// RENDER LOOP
// ============================================

function onWindowResize() {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
}

function animate() {
  renderer.setAnimationLoop(render);
}

function render(timestamp, frame) {
  const delta = clock.getDelta();

  // Update animation mixer
  if (mixer) {
    mixer.update(delta);
  }

  // Brightness fade-in (emissive intensity 0 → 1)
  if (brightnessFading && model) {
    const elapsed = performance.now() - brightnessFadeStart;
    const progress = Math.min(elapsed / BRIGHTNESS_FADE_DURATION, 1);
    // Smooth ease-out curve
    const intensity = 1 - Math.pow(1 - progress, 2);
    model.traverse((child) => {
      if (child.isMesh) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((mat) => {
          if ('emissiveIntensity' in mat) mat.emissiveIntensity = intensity;
        });
      }
    });
    if (progress >= 1) brightnessFading = false;
  }

  // Turntable rotation
  if (turntableEnabled && model && !renderer.xr.isPresenting && !modelPlaced) {
    model.rotation.y += turntableSpeed * delta;
  }

  // Update controls (only when not in AR)
  if (!renderer.xr.isPresenting) {
    controls.update();
  }

  // AR mode hit testing
  if (frame && renderer.xr.isPresenting) {
    const referenceSpace = renderer.xr.getReferenceSpace();
    const session = renderer.xr.getSession();

    if (!hitTestSourceRequested) {
      session.requestReferenceSpace('viewer').then((refSpace) => {
        session.requestHitTestSource({ space: refSpace }).then((source) => {
          hitTestSource = source;
        }).catch((err) => {
          console.warn('Hit test not available:', err);
        });
      }).catch((err) => {
        console.warn('Viewer reference space not available:', err);
      });

      session.addEventListener('end', () => {
        hitTestSourceRequested = false;
        hitTestSource = null;
      });

      hitTestSourceRequested = true;
    }

    if (hitTestSource) {
      const hitTestResults = frame.getHitTestResults(hitTestSource);
      if (hitTestResults.length > 0) {
        const hit = hitTestResults[0];
        const pose = hit.getPose(referenceSpace);

        if (!modelPlaced && model) {
          // Auto-place model on first detected surface (like iOS Quick Look)
          model.position.setFromMatrixPosition(
            new THREE.Matrix4().fromArray(pose.transform.matrix)
          );
          model.visible = true;
          modelPlaced = true;
          reticle.visible = false;
          console.log('Model auto-placed at:', model.position);
        } else if (!modelPlaced) {
          // Show reticle while waiting for model
          reticle.visible = true;
          reticle.matrix.fromArray(pose.transform.matrix);
        }
      }
    }

    // AR touch gestures: rotate (1 finger) and pinch-to-scale (2 fingers)
    if (modelPlaced && model && arTouches.size > 0) {
      const activeSources = [];
      for (const [inputSource] of arTouches) {
        const grip = frame.getPose(inputSource.targetRaySpace, referenceSpace);
        if (grip) {
          activeSources.push({
            inputSource,
            position: new THREE.Vector3(
              grip.transform.position.x,
              grip.transform.position.y,
              grip.transform.position.z
            ),
            direction: new THREE.Quaternion(
              grip.transform.orientation.x,
              grip.transform.orientation.y,
              grip.transform.orientation.z,
              grip.transform.orientation.w
            )
          });
        }
      }

      if (activeSources.length === 1 && arPrevTouchData && arPrevTouchData.count === 1) {
        // Single finger drag → rotate model
        const curr = activeSources[0].position;
        const prev = arPrevTouchData.positions[0];
        const deltaX = curr.x - prev.x;
        const deltaY = curr.y - prev.y;
        const sensitivity = 3.0;
        model.rotation.y += deltaX * sensitivity;
        model.rotation.x += deltaY * sensitivity;
        // Clamp X rotation to prevent flipping
        model.rotation.x = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, model.rotation.x));
      } else if (activeSources.length >= 2 && arPrevTouchData && arPrevTouchData.count >= 2) {
        // Two finger pinch → scale model
        const currDist = activeSources[0].position.distanceTo(activeSources[1].position);
        const prevDist = arPrevTouchData.positions[0].distanceTo(arPrevTouchData.positions[1]);
        if (prevDist > 0.001) { // avoid division by near-zero
          const ratio = currDist / prevDist;
          arModelScale *= ratio;
          // Clamp scale between 0.1x and 10x
          arModelScale = Math.max(0.1, Math.min(10, arModelScale));
          model.scale.setScalar(arModelScale);
        }
      }

      // Store current frame data for next frame's delta
      arPrevTouchData = {
        count: activeSources.length,
        positions: activeSources.map(s => s.position.clone())
      };
    }
  }

  // Render scene
  renderer.render(scene, camera);
}

console.log(`Oradea Art Nouveau WebXR Viewer V${VERSION} initialized`);
